/**
 * The first-login steps, moved from today's WelcomeScreen (src/legacy/pages/WelcomeScreen.tsx) with the same logic and
 * the same windows: WelcomeBack -> ProfileSetup (name, phone + code or the non-US/CA skip, photo) -> FirstMessage
 * (required) -> Subscription checkout (unless the fan already has access) -> PostPayment welcome.
 * Two fixes vs today: the "has a chat already?" check uses the real route (/api/messages/user/conversations, today's
 * /api/conversations does not exist), and after payment the fan lands in Text (today: /messaging, a dead route).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { WelcomeBackModal } from '@/components/auth/WelcomeBackModal'
import { ProfileSetupModal } from '@/components/auth/ProfileSetupModal'
import { FirstMessageModal } from '@/components/auth/FirstMessageModal'
import { SubscriptionOnboardingModal } from '@/components/auth/SubscriptionOnboardingModal'
import { PostPaymentWelcomeModal } from '@/components/auth/PostPaymentWelcomeModal'
import { api } from '~/api/client'

export function Onboarding() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState<'none' | 'welcome' | 'profile' | 'first' | 'pay' | 'paid'>('none')
  const [inviteToken, setInviteToken] = useState<string | null>(null)
  const started = useRef(false)

  async function needsCheckout(): Promise<boolean> {
    const lvl = await api<any>('/api/subscriptions/access/level')
    if (lvl.data?.hasAccess && (lvl.data.state === 'ACTIVE' || lvl.data.state === 'TRIALING')) return false
    if (user?.email) {
      const inv = await api<any>(`/api/subscriptions/invites/check-by-email?email=${encodeURIComponent(user.email)}`, { auth: false })
      if (inv.data?.hasInvite && inv.data.invite?.token) {
        localStorage.setItem('pending_invite_token', inv.data.invite.token)
        setInviteToken(inv.data.invite.token)
        return localStorage.getItem('needsWelcome') !== 'true'
      }
    }
    const [sub, acc] = await Promise.all([api<any>('/api/subscriptions/current'), api<any>('/api/subscriptions/access')])
    const s = sub.data?.subscription
    const needs = !s || ['incomplete', 'incomplete_expired', 'canceled', 'unpaid'].includes(s.status)
    if (needs && !acc.data?.hasAccess) return true
    localStorage.removeItem('pending_invite_token'); localStorage.removeItem('has_pending_subscription')
    return false
  }

  useEffect(() => {
    if (!user?.id || started.current) return
    const needsWelcome = localStorage.getItem('needsWelcome')
    const pendingInvite = localStorage.getItem('pending_invite_token')
    if (pendingInvite) setInviteToken(pendingInvite)
    const hasName = (user as any).firstName || (user as any).first_name
    if (needsWelcome === 'true' || !hasName) {
      started.current = true
      localStorage.removeItem('needsWelcome')
      setStep('welcome')
      return
    }
    started.current = true
    if (pendingInvite) localStorage.removeItem('pending_invite_token') // returning member with a stale token (today's fix)
    needsCheckout().then((yes) => { if (yes) setStep('pay') }).catch(() => {})
  }, [user])

  async function afterProfile() {
    const r = await api<any>('/api/messages/user/conversations')
    const hasChat = (r.data?.data?.length ?? 0) > 0
    if (hasChat) return checkoutIfNeeded()
    setStep('first')
  }
  async function checkoutIfNeeded() {
    // A fan who already has access goes straight in (today's international-phone fix)
    const lvl = await api<any>('/api/subscriptions/access/level')
    if (lvl.ok && lvl.data?.hasAccess === true) { setStep('none'); return }
    const p = localStorage.getItem('pending_invite_token'); if (p) setInviteToken(p)
    setStep('pay')
  }
  async function sendFirst(message: string) {
    await api('/api/subscriptions/onboarding/first-message', { method: 'POST', body: { message } })
    checkoutIfNeeded()
  }

  if (!user) return null
  return (
    <div className="tw-scope">
      <WelcomeBackModal isOpen={step === 'welcome'} onContinue={() => setStep('profile')} />
      <ProfileSetupModal isOpen={step === 'profile'} user={user} onComplete={() => { setStep('none'); afterProfile() }} />
      <FirstMessageModal isOpen={step === 'first'} onSubmit={sendFirst} />
      <SubscriptionOnboardingModal isOpen={step === 'pay'} userId={user.id} inviteToken={inviteToken} allowSkip={false}
        onComplete={() => { setInviteToken(null); setStep('paid') }} />
      <PostPaymentWelcomeModal isOpen={step === 'paid'} onContinue={() => { setStep('none'); navigate('/text'); window.location.reload() }} />
    </div>
  )
}
